import { ILoginData } from "@/domain/entities/login";
import { IResultData } from "@/domain/entities/shared/result";
import {
  ICompanyData,
  ICoordinateData,
  IFindIdData,
} from "@/domain/entities/user";
import {
  ICertInfo,
  IEmailDomainInfo,
  INoticeConfig,
  IQrCodeData,
  IShouldChangePasswordInfo,
  IUserInfo,
  IUserRemainBookCount,
  IUserVerifyEmployeeNumber,
} from "@/domain/entities/user/user.types";
import { Address, LineTypeCode, RankCode } from "@/types";
import { Result } from "neverthrow";
import { HttpError } from "./../shared/errors/httpError";
import { ParseError } from "./../shared/errors/parseError";
import { IReportHistory } from "@/domain/entities/report";

export interface ISignUpRequest {
  [key: string]: any;
}

export interface ITermsResult {
  agreeContent: string;
  agreeYn: boolean;
  agreeCode: string;
}

export interface ILoginRequest {
  serviceCode: string;
  loginId: string;
  password: string;
  fcmToken?: string;
}

export interface IChangePasswordRequest {
  currPassword: string;
  nextPassword: string;
  nextPasswordCert: string;
}

export interface IChangePhoneNumberRequest {
  phone: string;
  phoneCertNo: string;
}

export type HttpNoticeConfig = {
  memberUid: string;
  majorNoticeYn: boolean;
};

export type HttpNoticeConfigResponse = {
  data: HttpNoticeConfig;
};

export type HttpLogin = {
  memberUid: string;
  accessToken: string;
  refreshToken: string;
  tempPasswordYn: boolean;
  needPasswordChangeYn: boolean;
};

export type HttpLoginResponse = {
  data: HttpLogin;
};

export type HttpSocialLogin = {
  accessToken: string;
  refreshToken: string;
  memberUid: string;
};

export type HttpSocialLoginResponse = {
  data: HttpSocialLogin;
};

export type HttpFindId = {
  authKey: string;
  joinTs: string;
};

export type HttpFindIdResponse = {
  data: HttpFindId;
};

export type HttpUserInfoResponse = {
  data: HttpUserInfo;
};

export type HttpCompanyResponse = {
  data: HttpCompany[];
};

export type HttpCoordinateResponse = {
  documents: HttpCoordinate[];
};

export type HttpQrCodeResponse = {
  data: HttpQrCode;
};

export type HttpQrCode = {
  qrCode: string;
};

export type HttpCoordinate = {
  x: string;
  y: string;
};

export type HttpCompany = {
  uid: string;
  name: string;
  emailDomains: string[];
};

export enum HttpRankCode {
  /** 정규 */
  REG = "MUL-REG",
  /** 파트너 */
  BP = "MUL-BP",
  /** 기타 */
  NONE = "MUL-NONE",
}

export interface HttpReportSimpleInfo {
  reportName: string;
  count: number;
  color?: string;
}
export interface HttpReportHistory {
  memberUid: string;
  boardUid: string;
  serviceUid: string;
  allocUid: string;
  realOnDtm: string;
  boardDate: string;
  driveDate: string;
  lineRouteUid: string;
  lineRouteNo: string;
  lineRouteName: string;
  lineTurnUid: string;
  lineTypeName: string;
  lineTypeCode: LineTypeCode;
  campusName: string;
  campusCode: string;
  expectedBeginDtm: string;
  busOrder: string;
  carNo: string;
  vendorName: string;
  reportInfo: HttpReportSimpleInfo[];
}

export interface HttpReportHistoryListResponse {
  data: HttpReportHistory[];
}

export type HttpUserInfo = {
  uid: string;
  memberUid: string;
  authKey: string;
  email?: string;
  memberName: string;
  majorNoticeYn: boolean;
  name: string;
  alias: string;
  phone: string;
  clientUid?: string;
  clientName?: string;
  latestAccessTs: string;
  useRankCode: HttpRankCode;
  employeeNo: string;
  addressDetail: Address;
  deptName?: string;
  bpName?: string;
  dutyNote?: string;
  bookBlockEndDtm?: string;
  boardBlockEndDtm?: string;
  workTimeCode: string;
  workTimeName: string;
  passwordReuseCount: number;
  /** 사내 셔틀 탑승 차단 여부 */
  shuttleBlockYn?: boolean; // 셔틀 노선은 예약이 없기 때문에 셔틀 예약 차단 여부 값은 응답에 포함되지 않음
};

export type HttpEmailDomain = {
  uid: string; // 202008172223198680368214167CLI //소속사 식별자
  name: string; // 주식회사 //소속사 이름
  emailDomain: string; //
  clientGroupCodeUid: string; // 202007181132558672412344777CLG" //소속사 그룹 식별자
  clientGroupName: string; // CJ" // 소속사 그룹 이름
};

export type HttpEmailDomainInfo = {
  emailDomain: string;
  companies: HttpEmailDomain[];
};

export type HttpEmailDomainInfoResponse = {
  data: HttpEmailDomainInfo[];
};

export type HttpCertInfo = {
  memberUid: string;
  certYn: boolean;
  serviceUid: string;
  statusCode: string;
};

export type HttpCertInfoResponse = {
  data: HttpCertInfo;
};

export interface HttpUserRemainBookCountResponse {
  data: number;
}

export interface HttpShouldChangePasswordInfo {
  needPasswordChangeYn: boolean; // 비밀번호 만료 후, 변경 필요 여부
  needPasswordChangeAlarmYn: boolean; // 비밀번호 만료 젼, 변경 알림 필요 여부
  passwordValidEndDtm: string; // 2024-12-09T16:07:24.232000000+09:00,
  latestPasswordTs: string; // 2024-09-10T16:07:24.232000000+09:00
}

export interface HttpVerifyEmployeeNumber {
  name: string;
}

export interface HttpVerifyEmployeeNumberResponse {
  data: HttpVerifyEmployeeNumber;
}

export interface HttpShouldPasswordChangeInfoResponse {
  data: HttpShouldChangePasswordInfo;
}

export interface ILoginParser {
  toDomain(data: HttpLogin): Result<ILoginData, ParseError>;
}

export interface ISocialLoginParser {
  toDomain(data: HttpSocialLogin): Result<ILoginData, ParseError>;
}

export interface INoticeConfigParser {
  toDomain(data: HttpNoticeConfig): Result<INoticeConfig, ParseError>;
}

export interface IFindIdParser {
  toDomain(data: HttpFindId): Result<IFindIdData, ParseError>;
}

export interface IUserInfoParser {
  toDomain(data: HttpUserInfo): Result<IUserInfo, ParseError>;
  fromDomain(changedUserInfo: Record<string, any>): Record<string, any>;
}

export interface ICompanyParser {
  toDomain(data: HttpCompany): Result<ICompanyData, ParseError>;
}

export interface ICoordinateParser {
  toDomain(data: HttpCoordinate): Result<ICoordinateData, ParseError>;
}

export interface IQrCodeParser {
  toDomain(data: HttpQrCode): Result<IQrCodeData, ParseError>;
}

export interface IEmailDomainInfoParser {
  toDomain(data: HttpEmailDomainInfo): Result<IEmailDomainInfo, ParseError>;
}

export interface ICertInfoParser {
  toDomain(data: HttpCertInfo): Result<ICertInfo, ParseError>;
}

export interface IReportHistoryParser {
  toDomain(data: HttpReportHistory): Result<IReportHistory, ParseError>;
}

export interface IShouldChangePasswordInfoParser {
  toDomain(
    data: HttpShouldChangePasswordInfo
  ): Result<IShouldChangePasswordInfo, ParseError>;
}

export interface IRemainBookCountParser {
  toDomain(
    response: HttpUserRemainBookCountResponse
  ): Result<IUserRemainBookCount, ParseError>;
}

export interface IVerifyEmployeeNumberParser {
  toDomain(
    data: HttpVerifyEmployeeNumber
  ): Result<IUserVerifyEmployeeNumber, ParseError>;
}

export interface IUserRepository {
  checkDuplicateId(
    loginId: string
  ): Promise<Result<IResultData, ParseError | HttpError>>;

  sendEmailVerificationCode(
    email: string
  ): Promise<Result<IResultData, ParseError | HttpError>>;

  checkEmailVerificationCode(
    email: string,
    certNo: string
  ): Promise<Result<IResultData, ParseError | HttpError>>;

  sendPhoneVerificationCode(
    phoneNumber: string
  ): Promise<Result<IResultData, ParseError | HttpError>>;

  checkPhoneVerificationCode(
    phoneNumber: string,
    certNo: string
  ): Promise<Result<IResultData, ParseError | HttpError>>;

  verifyEmployeeNumber(
    eziokCertAuthValueEnc: string,
    employeeNo: string
  ): Promise<Result<IUserVerifyEmployeeNumber, ParseError | HttpError>>;

  signUp(
    rankCode: string,
    signupValue: Record<string, any>,
    termsResult: ITermsResult[]
  ): Promise<Result<IResultData, ParseError | HttpError>>;

  login(
    userId: string,
    password: string,
    pushToken: string
  ): Promise<Result<ILoginData, ParseError | HttpError>>;

  loginWithKakao(
    redirectUri: string,
    authCode: string
  ): Promise<Result<ILoginData, ParseError | HttpError>>;

  loginWithNaver(
    state: string,
    authCode: string
  ): Promise<Result<ILoginData, ParseError | HttpError>>;

  getNoticeConfig(): Promise<Result<INoticeConfig, ParseError | HttpError>>;

  setNoticeConfig(
    receiveNotice: boolean
  ): Promise<Result<INoticeConfig, ParseError | HttpError>>;

  findId(
    userName: string,
    phoneNumber: string
  ): Promise<Result<IFindIdData, ParseError | HttpError>>;

  findPassword(
    phoneNumber: string,
    userId?: string
  ): Promise<Result<IResultData, ParseError | HttpError>>;

  getUserInfo(): Promise<Result<IUserInfo, ParseError | HttpError>>;

  changePassword(
    passwordValues: Record<string, any>
  ): Promise<Result<IResultData, ParseError | HttpError>>;

  updatePassword(
    userId: string,
    currentPassword: string,
    newPassword: string
  ): Promise<Result<ILoginData, ParseError | HttpError>>;

  changePhoneNumber(
    phoneNumber: string,
    verificationCode: string
  ): Promise<Result<IResultData, ParseError | HttpError>>;

  checkIfPasswordNeedsChange(): Promise<
    Result<IShouldChangePasswordInfo, ParseError | HttpError>
  >;

  checkPassword(
    password: string
  ): Promise<Result<IResultData, ParseError | HttpError>>;

  changeMyInfo(
    myInfoValue: Record<string, any>
  ): Promise<Result<IResultData, ParseError | HttpError>>;

  updateToken(
    memberUid: string,
    refreshToken: string
  ): Promise<Result<ILoginData, ParseError | HttpError>>;

  updatePushToken(
    fcmToken: string
  ): Promise<Result<IResultData, ParseError | HttpError>>;

  getCompanyList(
    rankCode: RankCode
  ): Promise<Result<ICompanyData[], ParseError | HttpError>>;

  getAddressCoordinate(
    roadAddress: string
  ): Promise<Result<ICoordinateData, ParseError | HttpError>>;

  getQrCode(): Promise<Result<IQrCodeData, ParseError | HttpError>>;

  getEmailDomains(): Promise<
    Result<IEmailDomainInfo[], ParseError | HttpError>
  >;

  approveUserSignUp(
    certYn: boolean,
    token: string
  ): Promise<Result<ICertInfo, ParseError | HttpError>>;

  unlockAccount(
    loginId: string,
    eziokCertAuthValueEnc: string
  ): Promise<Result<IResultData, ParseError | HttpError>>;

  extendPasswordExpiration(
    memberUid: string
  ): Promise<Result<IResultData, ParseError | HttpError>>;

  getRemainBookCount(): Promise<
    Result<IUserRemainBookCount, ParseError | HttpError>
  >;

  getReportHistory(
    fromDate: string,
    toDate: string
  ): Promise<Result<IReportHistory[], ParseError | HttpError>>;
}
